// Code highlighted at build time. Code examples known at build time are listed by
// `*.static-code.ts` modules that export `listStaticCode()`. The build (vite/staticCode.ts, using
// scripts/highlight-static-code-loader.cjs) highlights those samples with Shiki and replaces each
// module with a `registerHighlightedCode(...)` call; the component that renders the examples imports
// the module for that side effect. StaticCode looks code up here first, so static examples render
// without highlighting at request time, on the server and during hydration alike.

/**
 * How code is shown: `block` on the dark code background (the code theme only), `inline` within
 * prose on the inline-code background, so it also needs light-scheme colors.
 */
export type HighlightVariant = "block" | "inline";

/** A code value a module renders unchanged, with the language it is highlighted as. */
export interface StaticCodeSample {
  value: string;
  language: string;
  variant?: HighlightVariant;
}

/** A highlighted token: its text and its classes from the code palette (CodePalette.ts). */
export interface HighlightedToken {
  content: string;
  /** None for text in the code's default color. */
  className?: string;
}

/** The light theme inline code uses alongside the code theme. */
export const INLINE_LIGHT_THEME = "github-light";

/** Languages the highlighter loads; anything else renders as plain text. */
export const CODE_LANGUAGES = [
  "javascript",
  "typescript",
  "tsx",
  "vue",
  "svelte",
  "python",
  "go",
  "ruby",
  "rust",
  "java",
  "php",
  "xml",
  "bash",
  "json",
] as const;

export type CodeLanguage = (typeof CODE_LANGUAGES)[number];

export const resolveCodeLanguage = (language: string): string =>
  (CODE_LANGUAGES as readonly string[]).includes(language) ? language : "text";

/** 53-bit string hash (cyrb53), so registered data need not repeat the code it highlights. */
const hash = (text: string) => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index++) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export const getHighlightedCodeKey = (
  value: string,
  language: string,
  variant: HighlightVariant = "block",
) => hash(`${variant === "inline" ? "inline:" : ""}${language}\n${value}`).toString(36);

/**
 * Registered data is a JSON string `[styles, entries]`, parsed on first lookup. Each style is a
 * token's class names (empty for none). Each entry encodes lines separated by `;`, and tokens as
 * `length.styleIndex` (base 36) separated by `,`; token text is sliced from the looked-up value.
 */
type EncodedChunk = [styles: string[], entries: Record<string, string>];

export const encodeHighlightedCode = (
  highlighted: (StaticCodeSample & { tokens: HighlightedToken[][] })[],
): string => {
  const styles: string[] = [];
  const styleIndexes = new Map<string, number>();
  const entries: Record<string, string> = {};
  for (const { value, language, variant, tokens } of highlighted) {
    entries[getHighlightedCodeKey(value, language, variant)] = tokens
      .map((line) =>
        line
          .map((token) => {
            const style = token.className ?? "";
            let index = styleIndexes.get(style);
            if (index === undefined) {
              index = styles.push(style) - 1;
              styleIndexes.set(style, index);
            }
            return `${token.content.length.toString(36)}.${index.toString(36)}`;
          })
          .join(","),
      )
      .join(";");
  }
  return JSON.stringify([styles, entries] satisfies EncodedChunk);
};

const decode = (value: string, styles: string[], encoded: string): HighlightedToken[][] | null => {
  let offset = 0;
  const lines = encoded.split(";").map((line, lineIndex) => {
    if (lineIndex > 0) offset += value.startsWith("\r\n", offset) ? 2 : 1;
    return line
      ? line.split(",").map((token) => {
          const [length, styleIndex] = token.split(".").map((part) => Number.parseInt(part, 36));
          const className = styles[styleIndex];
          const content = value.slice(offset, offset + length);
          offset += length;
          return className ? { content, className } : { content };
        })
      : [];
  });
  // A hash collision with a different value would not consume it exactly.
  return offset === value.length ? lines : null;
};

const pendingChunks: string[] = [];
const entries = new Map<string, { styles: string[]; encoded: string }>();

/** Called by modules the build loader has highlighted. */
export const registerHighlightedCode = (data: string) => {
  pendingChunks.push(data);
};

export const findHighlightedCode = (
  value: string,
  language: string,
  variant: HighlightVariant = "block",
): HighlightedToken[][] | null => {
  for (const chunk of pendingChunks.splice(0)) {
    const [styles, chunkEntries] = JSON.parse(chunk) as EncodedChunk;
    for (const [key, encoded] of Object.entries(chunkEntries))
      entries.set(key, { styles, encoded });
  }
  const entry = entries.get(getHighlightedCodeKey(value, language, variant));
  return entry ? decode(value, entry.styles, entry.encoded) : null;
};
